import { useEffect, useState } from 'react'
import apiClient from '@/utils/api'
import { entitiesCache, entitiesCacheKeyForReferencePickers } from '@/utils/entitiesCache'
import { ENTITIES_RELOAD_EVENT } from './flushAppAfterModuleChange'

export type KnownObjectNames =
  | { status: 'loading' }
  | { status: 'ready'; names: Set<string> }
  | { status: 'unavailable' }

/** Tree-less platform catalogs. Administration opens them; the designer
 *  registry never lists them, so a missing name prunes the tab on arrival. */
const PLATFORM_OBJECT_NAMES = ['Users', 'Roles'] as const

function namesFromPayload(payload: {
  catalogs?: Array<{ name?: string }>
  documents?: Array<{ name?: string }>
  registers?: Array<{ name?: string }>
}): Set<string> {
  const names = new Set<string>(PLATFORM_OBJECT_NAMES)
  for (const row of [
    ...(payload.catalogs ?? []),
    ...(payload.documents ?? []),
    ...(payload.registers ?? []),
  ]) {
    if (row?.name) names.add(row.name)
  }
  return names
}

/**
 * Live catalog+document+register names including sidebar-hidden objects.
 * `unavailable` = fetch failed — callers must fail open (do not close tabs).
 */
export function useKnownObjectNames(connectionId: string | undefined): KnownObjectNames {
  const [state, setState] = useState<KnownObjectNames>({ status: 'loading' })

  useEffect(() => {
    if (!connectionId) {
      setState({ status: 'unavailable' })
      return
    }

    let cancelled = false
    const key = entitiesCacheKeyForReferencePickers(connectionId)

    const load = async (opts?: { skipCache?: boolean }) => {
      if (!opts?.skipCache) {
        const cached = entitiesCache.get(key)
        if (cached) {
          if (!cancelled) setState({ status: 'ready', names: namesFromPayload(cached) })
          return
        }
      }
      try {
        const res = await apiClient.get('/v1/designer/entities', {
          params: { connection_id: connectionId, include_excluded: true },
        })
        const payload = (res?.data ?? res ?? {}) as {
          catalogs?: Array<{ name?: string }>
          documents?: Array<{ name?: string }>
          registers?: Array<{ name?: string }>
        }
        if (
          !Array.isArray(payload.catalogs) &&
          !Array.isArray(payload.documents) &&
          !Array.isArray(payload.registers)
        ) {
          if (!cancelled) setState({ status: 'unavailable' })
          return
        }
        entitiesCache.set(key, payload)
        if (!cancelled) setState({ status: 'ready', names: namesFromPayload(payload) })
      } catch {
        if (!cancelled) setState({ status: 'unavailable' })
      }
    }

    setState({ status: 'loading' })
    void load()

    const onReload = (e: Event) => {
      const detail = (e as CustomEvent).detail as { connectionId?: string } | undefined
      if (detail?.connectionId && detail.connectionId !== connectionId) return
      setState({ status: 'loading' })
      void load({ skipCache: true })
    }
    window.addEventListener(ENTITIES_RELOAD_EVENT, onReload)
    return () => {
      cancelled = true
      window.removeEventListener(ENTITIES_RELOAD_EVENT, onReload)
    }
  }, [connectionId])

  return state
}
